import React, { useRef, useLayoutEffect, useCallback, useImperativeHandle } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  position: relative;
  overflow: hidden; /* crops off the native horizontal scrollbar strip
                        (track + arrow buttons) that Scroller reserves at
                        its own bottom edge. Height is set imperatively in
                        JS to exactly the content height, excluding that
                        strip, so nothing is ever visible or reserved. */
`;

const Scroller = styled.div`
  && {
    /* && doubles the generated class -> higher specificity than a single
       legacy class selector (e.g. an old .trackx rule), so this wins the
       cascade even if that rule is also !important. */
    overflow-x: scroll !important;
    overflow-y: hidden !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;

    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
      -webkit-appearance: none !important;
    }
    &::-webkit-scrollbar-button {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }
    &::-webkit-scrollbar-track,
    &::-webkit-scrollbar-thumb {
      display: none !important;
      background: transparent !important;
    }
  }
`;

const Content = styled.div`
  display: inline-block;
  min-width: 100%;
`;

const FadeLeft = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  background: linear-gradient(to right, rgb(255 255 255 / 80%) 10%, transparent);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  &.is-visible {
    opacity: 1;
  }
`;

const FadeRight = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: ${({ $width }) => $width}px;
  background: linear-gradient(to left, rgb(255 255 255 / 90%) 20%, transparent);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  &.is-visible {
    opacity: 1;
  }
`;

const Thumb = styled.div`
  position: absolute;
  left: 0;
  bottom: ${({ $offset }) => $offset}px;
  height: ${({ $thickness }) => $thickness}px;
  border-radius: ${({ $thickness }) => $thickness / 2}px;
  background: ${({ $color }) => $color};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  will-change: opacity, left, width;
  &.is-visible {
    opacity: 1;
  }
`;

const DEFAULT_MIN_THUMB_WIDTH = 30;
const DEFAULT_HIDE_DELAY = 600;
const DEFAULT_FADE_WIDTH = 40;
const DEFAULT_FADE_COLOR = '#fff';

const TrackX = React.forwardRef(function TrackX({
  children,
  className,
  thumbColor = 'rgba(0, 0, 0, 0.35)',
  thickness = 6,
  offset = 2,
  hideDelay = DEFAULT_HIDE_DELAY,
  minThumbWidth = DEFAULT_MIN_THUMB_WIDTH,
  fadeWidth = DEFAULT_FADE_WIDTH,
}, forwardedRef) {
  const wrapRef = useRef(null);
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  const thumbRef = useRef(null);
  const fadeLeftRef = useRef(null);
  const fadeRightRef = useRef(null);
  const hideTimer = useRef(null);

  const setScrollerRef = useCallback((node) => {
    scrollerRef.current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  }, [forwardedRef]);

  useImperativeHandle(forwardedRef, () => ({
    get scroller() { return scrollerRef.current; },
    scrollTo: (...args) => scrollerRef.current?.scrollTo(...args),
    scrollBy: (...args) => scrollerRef.current?.scrollBy(...args),
    get scrollLeft() { return scrollerRef.current?.scrollLeft ?? 0; },
    set scrollLeft(v) { if (scrollerRef.current) scrollerRef.current.scrollLeft = v; },
  }), []);

  const update = useCallback(() => {
    const wrap = wrapRef.current;
    const scroller = scrollerRef.current;
    const thumb = thumbRef.current;
    const fadeLeft = fadeLeftRef.current;
    const fadeRight = fadeRightRef.current;
    if (!wrap || !scroller || !thumb) return;

    // Physically clip whatever height the native horizontal scrollbar
    // (including arrow buttons on classic Windows Chrome) is occupying
    // inside `scroller`'s own box. This guarantees no reserved space and
    // no visible native scrollbar regardless of whether the CSS hiding
    // above wins its cascade fight.
    wrap.style.height = `${scroller.clientHeight}px`;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    // Edge fades: right fade shows while there's still overflow content
    // to scroll into view; left fade shows once the user has scrolled
    // far enough that earlier content is out of view.
    if (fadeLeft && fadeRight) {
      if (maxScroll <= 0) {
        fadeLeft.classList.remove('is-visible');
        fadeRight.classList.remove('is-visible');
      } else {
        const scrollLeft = scroller.scrollLeft;
        fadeLeft.classList.toggle('is-visible', scrollLeft > 0);
        fadeRight.classList.toggle('is-visible', scrollLeft < maxScroll - 1);
      }
    }

    if (maxScroll <= 0) {
      thumb.style.width = '0px';
      return;
    }

    const trackWidth = wrap.clientWidth;
    const ratio = scroller.clientWidth / scroller.scrollWidth;
    const thumbWidth = Math.max(trackWidth * ratio, minThumbWidth);
    const maxThumbLeft = trackWidth - thumbWidth;
    const scrollRatio = scroller.scrollLeft / maxScroll;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.left = `${maxThumbLeft * scrollRatio}px`;
  }, [minThumbWidth]);

  const reveal = useCallback(() => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    thumb.classList.add('is-visible');
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      thumb.classList.remove('is-visible');
    }, hideDelay);
  }, [hideDelay]);

  const handleScroll = useCallback(() => {
    update();
    reveal();
  }, [update, reveal]);

  useLayoutEffect(() => {
    // useLayoutEffect (not useEffect) so the height clip is applied
    // before first paint - avoids a one-frame flash of the un-clipped
    // native scrollbar.
    update();

    const scroller = scrollerRef.current;
    const wrap = wrapRef.current;
    const content = contentRef.current;

    const resizeObserver = new ResizeObserver(update);
    if (scroller) resizeObserver.observe(scroller);
    if (wrap) resizeObserver.observe(wrap);
    if (content) resizeObserver.observe(content);

    window.addEventListener('resize', update);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', update);
      clearTimeout(hideTimer.current);
    };
  }, [update]);

  return (
    <Wrapper ref={wrapRef} className={className}>
      <Scroller ref={setScrollerRef} onScroll={handleScroll}>
        <Content ref={contentRef}>{children}</Content>
      </Scroller>
      <FadeLeft ref={fadeLeftRef} $width={fadeWidth} />
      <FadeRight ref={fadeRightRef} $width={fadeWidth} />

      <Thumb ref={thumbRef} $color={thumbColor} $thickness={thickness} $offset={offset} />
    </Wrapper>
  );
});

export default TrackX;
